<template>
  <div class="primary-cta-block">
    <div class="completion-divider" aria-hidden="true">
      <span />
      <small>{{ t('blog.detail.cta.completionLabel') }}</small>
      <span />
    </div>

    <section class="primary-cta-card" aria-labelledby="blog-primary-cta-title">
      <div class="cta-copy">
        <span class="cta-kicker">{{ t('blog.detail.cta.kicker') }}</span>
        <h2 id="blog-primary-cta-title">{{ t('blog.detail.cta.title') }}</h2>
        <p>{{ t('blog.detail.cta.description') }}</p>

        <ul class="cta-features" :aria-label="t('blog.detail.cta.featuresLabel')">
          <li v-for="featureKey in featureKeys" :key="featureKey">
            <span class="feature-check" aria-hidden="true">✓</span>
            {{ t(featureKey) }}
          </li>
        </ul>
      </div>

      <div class="cta-action">
        <NuxtLink :to="cta.href" class="primary-cta">
          <span>{{ cta.label }}</span>
          <span class="cta-arrow" aria-hidden="true">→</span>
        </NuxtLink>
        <small>{{ t('blog.detail.cta.hint') }}</small>
      </div>
    </section>
  </div>
</template>

<script setup>
const { t } = useI18n();

defineProps({
  cta: {
    type: Object,
    required: true
  }
});

const featureKeys = [
  'blog.detail.cta.formats',
  'blog.detail.cta.security',
  'blog.detail.cta.quota'
];
</script>

<style scoped lang="scss">
.primary-cta-block {
  margin-top: $spacing-xl;
}

.completion-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: $spacing-lg;
  color: $gray-600;

  span {
    flex: 1;
    height: 1px;
    background: $gray-200;
  }

  small {
    font-size: 12px;
  }
}

.primary-cta-card {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 28px;
  overflow: hidden;
  padding: 26px 28px;
  border: 1px solid rgba($primary-color, 0.32);
  border-radius: $border-radius-lg;
  background:
    radial-gradient(circle at 92% 8%, rgba($accent-color, 0.18), transparent 36%),
    linear-gradient(125deg, rgba($primary-color, 0.07), $white);

  &::before {
    position: absolute;
    inset: 0 auto 0 0;
    width: 4px;
    background: linear-gradient($primary-color, $accent-color);
    content: '';
  }
}

.cta-kicker {
  display: block;
  margin-bottom: 7px;
  color: $primary-dark;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
}

.cta-copy {
  min-width: 0;

  h2 {
    margin: 0;
    color: $gray-800;
    font-size: clamp(20px, 3vw, 26px);
    line-height: 1.35;
  }

  p {
    margin: $spacing-sm 0 14px;
    color: $gray-600;
    font-size: 14px;
    line-height: 1.6;
  }
}

.cta-features {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing-sm 15px;
  margin: 0;
  padding: 0;
  color: $gray-600;
  font-size: 12px;
  list-style: none;

  li {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
}

.feature-check {
  color: $primary-dark;
}

.cta-action {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: $spacing-sm;
  min-width: 178px;

  > small {
    color: $gray-600;
    font-size: 11px;
    text-align: center;
  }
}

.primary-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 46px;
  padding: 11px 18px;
  border-radius: $border-radius-md;
  background: $primary-color;
  color: $white;
  text-decoration: none;
  font-weight: 600;
  box-shadow: 0 8px 20px rgba($primary-color, 0.26);
  transition: background 0.16s ease, transform 0.16s ease;

  &:hover {
    background: $primary-dark;
    color: $white;
    text-decoration: none;
    transform: translateY(-1px);
  }
}

@media (max-width: 768px) {
  .primary-cta-card {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 22px 20px;
  }

  .cta-features {
    display: grid;
    grid-template-columns: 1fr;
  }

  .cta-action {
    min-width: 0;
  }

  .primary-cta {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .primary-cta {
    transition: none;
  }
}
</style>
